import type { ReactNode } from 'react'
import { Suspense } from 'react'
import { Outlet } from 'react-router-dom'

import { FileSearchRoute } from '@jotta/files/FileSearchRoute'
import { LegacyPublicShareRoute } from '@jotta/files-public'
import { WithAuth } from '@jotta/grpc-connect-client/WithAuth'
import { PrivatePhotoRoute, SharedPhotosRoute } from '@jotta/photos/routes'
import { createRouter } from '@jotta/router'
import { excludesFalse } from '@jotta/types/TypeUtils'
import { ErrorPage } from '@jotta/ui/ErrorPage'
import { lazyWithRetry } from '@jotta/ui/lazyWithRetry'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { Redirect } from '@jotta/ui/Redirect'
import { useBrandCode } from '@jotta/ui/useBrandTheme'
import { redirectToLogout } from '@jotta/utils/auth'
import { captureIapQuery } from '@jotta/utils/iapSession'
import { sentryCreateBrowserRouter } from '@jotta/utils/initializeSentry'

import { AfterTerminate } from '../auth/AfterTerminate'
import { BusinessSignup } from '../auth/BusinessSignup'
import { DeviceLoginRoute } from '../auth/DeviceLoginRoute'
import { LoginRoute } from '../auth/LoginRoute'
import { ReferralRoute } from '../auth/ReferralRoute'
import { SignupAccepted } from '../auth/SignupAccepted'
import { SignupRoute } from '../auth/SignupRoute'
import { VoucherSignup } from '../auth/VoucherSignup'
import { bizRoutes } from '../biz/BizRoutes'
import { PrivateLayout } from '../components/Layout/PrivateLayout'
import { PublicLayout } from '../components/Layout/PublicLayout'
import { ReferFriendRoute } from '../referral/ReferFriendRoute'
import { UpgradeAccepted } from '../upgrade/UpgradeAccepted'
import { UpgradeRoute } from '../upgrade/UpgradeRoute'

import { FamilyInviteRoute } from './familyinvite/FamilyInviteRoute'
import {
  acceptFolderInviteRoute,
  autoAcceptFolderInviteRoute,
} from './folderinvite/AcceptFolderInviteRoute'
import { FolderInviteRoute } from './folderinvite/FolderInviteRoute'
import { SetPasswordRoute } from './password/SetPasswordRoute'
import { PaymentStatusRoute } from './payment/PaymentStatusRoute'
import { PayPalPaymentRoute } from './payment/PayPalPaymentRoute'
import { PublicPhotoRouteContainer } from './photo/PublicPhotoRouteContainer'
import { AppsRoute } from './public/AppsRoute'
import { appsRouteLoader } from './public/appsRouteLoader'
import { contentRefRoute } from './public/ContentRefRoute'
import { HomeRoute } from './public/HomeRoute'
import { VerifyEmailRoute } from './web/VerifyEmailRoute'
import { webFileGotoRoute } from './web/WebFileGotoRoute'
import { AccessibilityRoute } from './AccessibilityRoute'
import { CookiesRoute } from './CookiesRoute'
import { DownloadPageRoute } from './DownloadPageRoute'
import { PrivacyRoute } from './PrivacyRoute'
import { ReadLockBlock } from './ReadLockBlock'
import { Root } from './Root'
import { rootLoader } from './rootLoader'
import { SupportRoute } from './SupportRoute'
import { TermsRoute } from './TermsRoute'
import { VersionPageRoute } from './VersionPageRoute'

const PublicShareRoute = lazyWithRetry(
  () => import(/* webpackChunkName: "PublicFiles" */ '@jotta/files-public'),
)
const DevRoutes = lazyWithRetry(
  () => import(/* webpackChunkName: "DevRoutes" */ './dev/DevRoutes'),
)
const SettingsPage = lazyWithRetry(
  () => import(/* webpackChunkName: "Settings" */ '@jotta/settings'),
)
const FileLayout = lazyWithRetry(
  () => import(/* webpackChunkName: "filelayout" */ '@jotta/files/FileLayout'),
)

const FileRoute = lazyWithRetry(
  () => import(/* webpackChunkName: "files" */ '@jotta/files/FileRoute'),
)

function JottaOnly({ children }: { children: ReactNode }) {
  const brandCode = useBrandCode()
  if (!brandCode) {
    return <LoadingOverlay id="RootRoutes" />
  } else if (brandCode === 'JOTTACLOUD') {
    return children
  } else {
    return <ErrorPage notFound />
  }
}

captureIapQuery(window.location.search)

export const router = createRouter(sentryCreateBrowserRouter, [
  // #region Contentref redirects
  ...contentRefRoute,
  // #endregion Contentref redirects
  {
    path: '/',
    id: 'root',
    loader: rootLoader,
    element: <Root />,
    children: [
      // #region Misc routes
      {
        index: true,
        id: 'home',
        element: <HomeRoute />,
      },
      {
        path: 'download',
        id: 'download',
        element: <DownloadPageRoute />,
      },
      {
        path: 'version',
        id: 'version',
        element: <VersionPageRoute />,
      },
      {
        path: 'signup/:productCode?',
        id: 'signup',
        element: <SignupRoute />,
      },
      {
        path: 'login/:loginToken?',
        id: 'login',
        element: <LoginRoute />,
      },
      {
        path: 'referral/:referralCode',
        id: 'referral',
        element: <ReferralRoute />,
      },
      {
        path: 'web/refer-friend',
        id: 'refer',
        element: (
          <WithAuth>
            <ReferFriendRoute />
          </WithAuth>
        ),
      },
      {
        path: '2gs',
        id: '2gs',
        element: (
          <JottaOnly>
            <VoucherSignup customerGroupOverride="2GS" />
          </JottaOnly>
        ),
      },
      {
        path: 'elkjop-business',
        id: 'elkjop-business',
        element: (
          <JottaOnly>
            <BusinessSignup customerGroupOverride="ELKJOP_B2B" />
          </JottaOnly>
        ),
      },
      {
        path: 'elgiganten-se-business',
        id: 'elgiganten-se-business',
        element: (
          <JottaOnly>
            <BusinessSignup customerGroupOverride="ELGIGANTEN_S_B2B" />
          </JottaOnly>
        ),
      },
      {
        path: 'elgiganten-dk-business',
        id: 'elgiganten-dk-business',
        element: (
          <JottaOnly>
            <BusinessSignup customerGroupOverride="ELGIGANTEN_D_B2B" />
          </JottaOnly>
        ),
      },
      {
        path: 'gigantti-business',
        id: 'gigantti-business',
        element: (
          <JottaOnly>
            <BusinessSignup customerGroupOverride="GIGANTTI_B2B" />
          </JottaOnly>
        ),
      },
      {
        path: 'appletv',
        id: 'appletv',
        element: (
          <WithAuth>
            <DeviceLoginRoute />
          </WithAuth>
        ),
      },
      {
        path: '/web/appletv',
        id: '/web/appletv',
        element: (
          <WithAuth>
            <DeviceLoginRoute />
          </WithAuth>
        ),
      },
      {
        path: '/account/upgrade',
        id: '/account/upgrade',
        element: (
          <WithAuth>
            <UpgradeRoute convertIntent={false} />
          </WithAuth>
        ),
      },
      {
        id: 'apps',
        path: '/apps',
        loader: appsRouteLoader,
        element: <AppsRoute fallbackPath="/download" />,
      },
      {
        id: 'start',
        path: '/start',
        loader: appsRouteLoader,
        element: <AppsRoute fallbackPath="/web/sync" />,
      },
      {
        path: '/p/:username/:shareId/*',
        element: <LegacyPublicShareRoute />,
      },
      {
        path: '/p/:username/:shareId/:action',
        element: <LegacyPublicShareRoute />,
      },
      {
        path: '/privacy/:brandAlias?/:fileName?',
        id: '/privacy',
        element: <PrivacyRoute />,
      },
      {
        path: '/terms/:brandAlias?/:fileName?',
        id: '/terms',
        element: <TermsRoute />,
      },
      {
        path: '/terms-and-conditions',
        id: '/terms-and-conditions',
        element: <TermsRoute />,
      },
      {
        path: '/accessibility/:brandAlias?/:fileName?',
        id: '/accessibility',
        element: <AccessibilityRoute />,
      },
      {
        path: '/cookies',
        id: '/cookies',
        element: (
          <PublicLayout section="cookies">
            <CookiesRoute />
          </PublicLayout>
        ),
      },
      {
        path: '/web/payment/status/:flowId',
        element: <PaymentStatusRoute />,
      },
      {
        path: '/web/payment/paypal/:orderNumber',
        element: (
          <WithAuth>
            <PayPalPaymentRoute />
          </WithAuth>
        ),
      },
      {
        path: '/f/:inviteCode',
        element: <FamilyInviteRoute />,
      },
      {
        // B2 redirects Telia Cloud here AFTER successful accept
        path: '/familyinvite/confirm/:inviteCode',
        element: <FamilyInviteRoute />,
      },
      {
        path: '/familyinvite/:inviteCode',
        element: <FamilyInviteRoute />,
      },
      {
        path: '/web/signupAccepted/:productCode?/:interval?',
        element: <SignupAccepted />,
      },
      {
        path: '/web/upgradeAccepted/:productCode?/:interval?',
        element: <UpgradeAccepted />,
      },
      {
        path: '/folderInvite/:inviteCode',
        element: <FolderInviteRoute />,
      },
      {
        path: '/support',
        element: <SupportRoute />,
      },
      {
        path: '/web/logout',
        loader: async () => {
          redirectToLogout(`${window.location.origin}/web/sync`)
          return null
        },
        element: <LoadingOverlay id="LogutRedirect" open />,
      },
      {
        path: '/web/verify_email/:verificationCode',
        element: <VerifyEmailRoute />,
      },
      {
        path: '/web/reset/:resetKey',
        element: <SetPasswordRoute type="reset" />,
      },
      {
        path: '/start/:resetKey',
        element: <SetPasswordRoute />,
      },
      {
        path: '/web/afterTerminate',
        element: <AfterTerminate />,
      },
      // #endregion Misc routes
      // #region Biz
      bizRoutes,
      // #endregion Biz
      // #region Settings
      {
        path: 'web',
        element: (
          <WithAuth>
            <PrivateLayout />
          </WithAuth>
        ),
        children: [
          {
            path: 'settings',
            element: <Redirect to="/web/account" />,
          },
          {
            path: 'account',
            element: (
              <Suspense
                fallback={
                  <LoadingOverlay
                    id="SettingsPage.Account"
                    open
                    position="absolute"
                  />
                }
              >
                <SettingsPage routeContext="account" />
              </Suspense>
            ),
          },
          {
            path: 'secure',
            element: (
              <Suspense
                fallback={
                  <LoadingOverlay
                    id="SettingsPage.Secure"
                    open
                    position="absolute"
                  />
                }
              >
                <SettingsPage routeContext="secure" />
              </Suspense>
            ),
          },
          {
            path: 'billing',
            element: (
              <Suspense
                fallback={
                  <LoadingOverlay
                    id="SettingsPage.Billing"
                    open
                    position="absolute"
                  />
                }
              >
                <SettingsPage routeContext="billing" />
              </Suspense>
            ),
          },
          {
            path: 'connected',
            element: (
              <Suspense
                fallback={
                  <LoadingOverlay
                    id="SettingsPage.Connected"
                    open
                    position="absolute"
                  />
                }
              >
                <SettingsPage routeContext="connected" />
              </Suspense>
            ),
          },
          {
            path: 'family',
            element: (
              <Suspense
                fallback={
                  <LoadingOverlay
                    id="SettingsPage.Family"
                    open
                    position="absolute"
                  />
                }
              >
                <SettingsPage routeContext="family" />
              </Suspense>
            ),
          },
        ],
      },
      ...[
        'web/upgrade/:productCode?',
        'web/convertTerm',
        'web/expire',
        'web/renew',
      ].map((path, index) => {
        return {
          path,
          element: (
            <WithAuth>
              <UpgradeRoute convertIntent={index > 0} />
            </WithAuth>
          ),
        }
      }),
      {
        path: 'web',
        element: (
          <WithAuth>
            <PrivateLayout />
          </WithAuth>
        ),
        children: [webFileGotoRoute],
      },

      // #endregion Authenticated web routes
      // #region File routes
      {
        path: 'web/*',
        id: 'files',
        element: (
          <WithAuth>
            <PrivateLayout />
          </WithAuth>
        ),
        children: [
          autoAcceptFolderInviteRoute,
          acceptFolderInviteRoute,
          {
            id: 'filelayout',
            element: (
              <ReadLockBlock>
                <FileLayout />
              </ReadLockBlock>
            ),
            children: [
              {
                id: 'search',
                path: 'search/:viewMode/:sortBy/:query/*',
                element: <FileSearchRoute />,
              },
              {
                id: 'fileroute',
                path: ':context?/:viewMode?/:sortBy?/*?',
                element: <FileRoute />,
              },
            ],
          },
        ],
      },
      // #endregion File routes
      // #region Public shared files
      {
        id: 'sharedfiles',
        path: 's/*',
        element: (
          <PublicLayout section="sharedfiles">
            <PublicShareRoute />
          </PublicLayout>
        ),
      },
      // #endregion Public shared files
      // #region All photo routes
      {
        element: <PublicPhotoRouteContainer />,
        children: [SharedPhotosRoute],
      },
      {
        element: (
          <WithAuth>
            <PrivateLayout>
              <ReadLockBlock>
                <Outlet />
              </ReadLockBlock>
            </PrivateLayout>
          </WithAuth>
        ),
        children: [PrivatePhotoRoute],
      },
      // #endregion All photo routes
      // #region Dev and test routes
      process.env.NODE_ENV === 'development' && {
        path: 'dev/*',
        element: (
          <Suspense fallback={<LoadingOverlay id="DevRoutes" open />}>
            <DevRoutes />
          </Suspense>
        ),
      },
      // #endregion Dev and test routes
      {
        path: '*',
        element: <ErrorPage notFound />,
      },
    ].filter(excludesFalse),
  },
])
